import type { UseQueryResult } from '@tanstack/react-query';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import dayjs from '~/timeInit';

import type {
  OpportunityRelatedInclusion,
  OpportunityRelatedRecords,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { PreviousOpportunityChoice } from '../../copyFromPrevious/copyFromPreviousModel';
import type { RoomAssetLink } from '../packageOptions/types';

const RECENT_DAYS = 365;
const MAX_OPPORTUNITIES_READ = 10;

// This Opportunity, the latest other one that has closed, and any closing within or after the last
// year. A planned Opportunity's close date is only expected, so it can't be the latest deal that ran.
// Choices (the other Opportunities) come newest first.
export function opportunityIdsToRead(
  currentOpportunityId: string,
  choices: Array<PreviousOpportunityChoice>,
  today: string,
): Array<string> {
  const since = dayjs.utc(today).subtract(RECENT_DAYS, 'day').format('YYYY-MM-DD');
  const latestClosed = choices.find(({ closeDate }) => !!closeDate && closeDate <= today);
  const withinYear = choices.filter(({ closeDate }) => !!closeDate && closeDate >= since);
  const others = uniq(compact([latestClosed?.id, ...withinYear.map(({ id }) => id)]));
  return [currentOpportunityId, ...others.slice(0, MAX_OPPORTUNITIES_READ)];
}

export function usedAssetSfids(inclusionLists: Array<Array<OpportunityRelatedInclusion> | undefined>): Set<string> {
  return new Set(
    inclusionLists.flatMap((inclusions = []) => inclusions.flatMap(({ asset__c }) => (asset__c ? [asset__c] : []))),
  );
}

// No reads (nothing to check yet) must not become an empty set, which would hide every asset.
export function usedSfidsOnceLoaded(
  results: Array<Pick<UseQueryResult<OpportunityRelatedRecords>, 'data' | 'isLoading' | 'isSuccess'>>,
) {
  return {
    usedSfids:
      results.length > 0 && results.every(({ isSuccess }) => isSuccess)
        ? usedAssetSfids(results.map(({ data }) => data?.inclusions))
        : undefined,
    isLoading: results.some(({ isLoading }) => isLoading),
  };
}

// An asset not yet in Salesforce, or one this session already links, is always shown.
export function recentAssets(
  assets: Array<RoomAssetLink>,
  usedSfids: Set<string>,
  keepIds: Set<string>,
): Array<RoomAssetLink> {
  return assets.filter((asset) => !asset.sfid || usedSfids.has(asset.sfid) || keepIds.has(asset.id));
}
